import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { AuthShell } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const nav = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Le mot de passe doit faire au moins 8 caractères.");
      return;
    }
    if (password !== confirm) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Mot de passe mis à jour", { description: "Connectez-vous." });
      nav("/login", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthShell title="Lien invalide" subtitle="Le lien de réinitialisation est incomplet.">
        <Link to="/forgot-password" className="text-[#C4553B] hover:underline">
          Demander un nouveau lien
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Nouveau mot de passe"
      subtitle="Choisissez un mot de passe fort (8 caractères minimum)."
    >
      <form onSubmit={onSubmit} className="space-y-4" data-testid="reset-form">
        <div className="space-y-2">
          <Label className="text-slate-300">Nouveau mot de passe</Label>
          <PasswordInput
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="border-slate-700 bg-slate-950/40 text-slate-100"
            testIdPrefix="reset-password"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300">Confirmer</Label>
          <PasswordInput
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="border-slate-700 bg-slate-950/40 text-slate-100"
            testIdPrefix="reset-confirm"
          />
        </div>
        {error && (
          <div className="rounded border border-red-500/30 bg-red-500/10 p-2.5 text-sm text-red-300" data-testid="reset-error">
            {error}
          </div>
        )}
        <Button
          type="submit"
          disabled={loading}
          className="w-full bg-[#C4553B] text-white hover:bg-[#A8402C]"
          data-testid="reset-submit-btn"
        >
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Enregistrer
        </Button>
      </form>
    </AuthShell>
  );
}
